import * as React from 'react'
import { Loader2 } from 'lucide-react'
import type { BillingCardSetupDto } from '@lib/dto/billing.dto'
import type { Uuid } from '@lib/validators/params'
import { toast } from '@/components/ui/toast'
import { useBillingActions, useCardSetup } from '@/hooks/useBilling'

/** How long to wait for Stripe to settle a card setup before saying it can take a minute. */
const GIVE_UP_MS = 30_000

/** Says how a card setup back from Stripe ended, and returns true once it has; false while it's worth waiting. */
function announce(setup: BillingCardSetupDto | undefined, unreadable: boolean, timedOut: boolean): boolean {
  switch (setup?.status) {
    case 'succeeded':
      toast.success(setup.pendingAutoReload ? 'Card saved, and auto-reload is on' : 'Card saved')
      return true
    case 'cancelled':
      toast('The card wasn’t saved.')
      return true
    case 'expired':
    case 'failed':
      toast('The card wasn’t saved. Try again.')
      return true
    default:
      if (!unreadable && !timedOut) return false
      toast('Saved the card? It can take a minute to show here.')
      return true
  }
}

interface CardSetupReturnProps {
  setupId: Uuid
  /** Stripe's back link: the Owner left without saving a card. */
  cancelled: boolean
  /** Clears the setup from the URL once its outcome is shown. */
  onDone: () => void
}

/**
 * Back from Stripe's card page (`?card=<id>`): waits for the card to be
 * saved, then says how it went. From the back link it closes the page first;
 * a card saved in the meantime is kept.
 */
export function CardSetupReturn({ setupId, cancelled, onDone }: CardSetupReturnProps) {
  const { cancelCardSetup, refresh } = useBillingActions()
  const polled = useCardSetup(cancelled ? null : setupId)
  const { mutate: cancel } = cancelCardSetup
  const [timedOut, setTimedOut] = React.useState(false)
  const cancelSentRef = React.useRef(false)
  const doneRef = React.useRef(false)
  const setup = cancelled ? cancelCardSetup.data : polled.data
  const unreadable = cancelled ? cancelCardSetup.isError : polled.isError

  React.useEffect(() => {
    if (!cancelled || cancelSentRef.current) return
    cancelSentRef.current = true
    cancel(setupId)
  }, [cancelled, setupId, cancel])

  React.useEffect(() => {
    const timer = window.setTimeout(() => setTimedOut(true), GIVE_UP_MS)
    return () => window.clearTimeout(timer)
  }, [])

  React.useEffect(() => {
    if (doneRef.current || !announce(setup, unreadable, timedOut)) return
    doneRef.current = true
    if (setup?.status === 'succeeded') void refresh()
    onDone()
  }, [setup, unreadable, timedOut, refresh, onDone])

  return (
    <p role="status" className="flex items-center gap-2 text-body text-muted-foreground">
      <Loader2 className="size-4 animate-spin" />
      {cancelled ? 'Closing the card page…' : 'Saving your card…'}
    </p>
  )
}
